import React from 'react';
import { formatCurrency, currencySymbol } from '../../utils/formatters';

const variants = [
  {
    key: 'wealth_gap_primary',
    h1: "What’s your Wealth Gap?",
    sub: "Discover the difference between the wealth you have today and the wealth you’ll need to live the life you want. Your personalized gap report is just a click away.",
    cta: "Reveal My Wealth Gap",
    alt: "Show Me My Number",
  },
  {
    key: 'leaving_money_on_table',
    h1: "How much money could you be leaving on the table?",
    sub: "Most owners underestimate the true value locked in their practice. See where you stand today — and how much you could gain by closing your Wealth Gap.",
    cta: "Find Out What I’m Missing",
    alt: "Uncover Hidden Value",
  },
  {
    key: 'on_track_readiness',
    h1: "Are you on track to fund your future?",
    sub: "Your Wealth Gap shows the shortfall between what you have and what you’ll need for financial independence. Uncover your number in minutes.",
    cta: "Check My Readiness",
    alt: "See If I’m On Track",
  },
  {
    key: 'peer_compare',
    h1: "Where do you stack up against your peers?",
    sub: "Advisors who complete their Wealth Gap checkup often discover hidden value worth 20–40% of their practice. Find out how you compare.",
    cta: "Compare My Wealth Gap",
    alt: "See How I Rank",
  },
];

function formatMoney(n) {
  try {
    return formatCurrency(Number(n || 0));
  } catch (_) {
    return `${currencySymbol()}${Math.round(Number(n || 0)).toLocaleString()}`;
  }
}

const WealthGapChart = ({ exampleToday = 446404, exampleGoal = 1055645, compact = false }) => {
  const gap = Math.max(exampleGoal - exampleToday, 0);
  const leftX = 5, rightX = 95; // svg percent coords
  const baseY = 65; // baseline
  const todayY = 62; // today band height
  const goalY = 22; // goal band height
  const chartHeights = compact
    ? 'h-[10rem] sm:h-[11rem] md:h-[12rem] lg:h-[13rem]'
    : 'h-[14rem] sm:h-[16rem] md:h-[17rem] lg:h-[18rem]';
  return (
    <svg viewBox="0 0 100 70" className={`w-full ${chartHeights}`}>
      <defs>
        <linearGradient id="wgTodayFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--wg-today,#5B6B7A)" stopOpacity="0.22" />
          <stop offset="100%" stopColor="var(--wg-today,#5B6B7A)" stopOpacity="0.06" />
        </linearGradient>
        <linearGradient id="wgGapFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--wg-gap,#DC5A3F)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--wg-gap,#DC5A3F)" stopOpacity="0.18" />
        </linearGradient>
        <linearGradient id="wgGoalFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--wg-target,#1F9D55)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--wg-target,#1F9D55)" stopOpacity="0.18" />
        </linearGradient>
      </defs>

      {/* baseline */}
      <line x1={leftX} y1={baseY} x2={rightX} y2={baseY} stroke="currentColor" opacity="0.08" />

      {/* today area */}
      <path d={`M ${leftX},${todayY} L ${rightX},${todayY} L ${rightX},${baseY} L ${leftX},${baseY} Z`} fill="url(#wgTodayFill)" />

      {/* gap area */}
      <path d={`M ${leftX},${goalY + 8} L ${rightX},${goalY} L ${rightX},${todayY} L ${leftX},${todayY} Z`} fill="url(#wgGapFill)" />

      {/* goal thin band */}
      <path d={`M ${leftX},${goalY} L ${rightX},${goalY} L ${rightX},${goalY - 1} L ${leftX},${goalY - 1} Z`} fill="url(#wgGoalFill)" />

      {/* goal dashed */}
      <line x1={leftX} y1={goalY} x2={rightX} y2={goalY} stroke="var(--wg-target,#1F9D55)" strokeWidth="0.6" strokeDasharray="2 2" />

      {/* markers and labels */}
      <circle cx={30} cy={todayY} r={1.6} fill="var(--wg-today,#5B6B7A)" />
      <text x={30} y={todayY + 6} textAnchor="middle" fontSize="3.2" fill="currentColor" opacity="0.85">You are here</text>

      <circle cx={82} cy={goalY} r={1.6} fill="var(--wg-target,#1F9D55)" />
      <text x={82} y={goalY - 5} textAnchor="middle" fontSize="3.2" fill="currentColor" opacity="0.85">Goal</text>

      {/* gap pill */}
      <rect x={44} y={goalY + 18} width={40} height={12} rx={2.5} fill="var(--wg-gap,#DC5A3F)" opacity="0.1" />
      <text x={64} y={goalY + 26} textAnchor="middle" fontSize="3.8" fill="var(--wg-gap,#DC5A3F)" style={{ fontWeight: 700 }}>
        Wealth Gap: {formatMoney(gap)}
      </text>

      {/* side values */}
      <text x={leftX + 2} y={todayY - 4} fontSize="3.4" fill="currentColor" opacity="0.8">Today: {formatMoney(exampleToday)}</text>
      <text x={rightX - 26} y={goalY - 8} fontSize="3.4" fill="currentColor" opacity="0.8">Goal: {formatMoney(exampleGoal)}</text>
    </svg>
  );
};

const AssessmentPromptEmptyState = ({ placement = 'value_tab', compact = false }) => {
  const [choice] = React.useState(() => variants[Math.floor(Math.random() * variants.length)]);
  React.useEffect(() => {
    try { if (window.ahoy?.track) window.ahoy.track('wealth_gap_empty_state_viewed', { placement, variant: choice.key }); } catch (_) {}
  }, [placement, choice]);

  const startAssessment = (variant) => {
    try { if (window.ahoy?.track) window.ahoy.track('Assessment CTA Click', { placement, variant: choice.key, button: variant }); } catch (_) {}
    window.location.href = '/succession/plans';
  };

  return (
    <div className={`bg-white rounded-xl border border-succession-border ${compact ? 'p-4' : 'p-6'} text-center`}>
      <div className="max-w-3xl mx-auto">
        <h3 className={`${compact ? 'text-lg' : 'text-2xl'} font-semibold text-succession-text-primary mb-1`}>{choice.h1}</h3>
        <p className={`text-succession-text-secondary ${compact ? 'mb-0.5 text-sm' : 'mb-1'}`}>{choice.sub}</p>
        <div className={`relative mx-auto ${compact ? 'mb-2' : 'mb-3'} rounded-2xl ${compact ? 'pt-0.5 pb-2 px-3' : 'pt-1 pb-3 px-4'} bg-[var(--card-bg,theme(colors.white))] shadow-sm dark:bg-neutral-900`}>
          <WealthGapChart compact={compact} />
        </div>
        <div className={`${compact ? 'mt-2' : 'mt-3'} flex items-center justify-center gap-3`}>
          <button
            className={`inline-flex items-center gap-2 ${compact ? 'px-4 py-2 text-sm' : 'px-5 py-3'} bg-succession-primary text-white rounded-lg font-medium hover:bg-succession-primary/90`}
            onClick={() => { try { if (window.ahoy?.track) window.ahoy.track('wealth_gap_start_clicked', { source: 'empty_state', button: 'primary', variant: choice.key }); } catch (_) {} startAssessment('primary'); }}
          >
            {choice.cta}
          </button>
          <button
            className={`inline-flex items-center gap-2 ${compact ? 'px-4 py-2 text-sm' : 'px-5 py-3'} border border-succession-border text-succession-text-primary rounded-lg font-medium hover:bg-white`}
            onClick={() => { try { if (window.ahoy?.track) window.ahoy.track('wealth_gap_start_clicked', { source: 'empty_state', button: 'alt', variant: choice.key }); } catch (_) {} startAssessment('alt'); }}
          >
            {choice.alt}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentPromptEmptyState;


